import { useState } from "react";
import {
  Users,
  Briefcase,
  DollarSign,
  FileText,
  LogOut,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  Flag,
  Search,
  Mail,
} from "lucide-react";

import ContactMessages from "./ContactMessages";
import UsersTab from "./UsersTab";
import JobsTab from "./JobsTab";
import StatsCards from "./StatsCards";
import ReviewsTab from "./ReviewsTab";
import PaymentsTab from "./PaymentsTab";
import ExportTab from "./ExportTab";

export default function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState("dashboard");


  const stats = {
    totalUsers: 1240,
    customers: 980,
    taskers: 260,
    pendingVerifications: 12,
    activeJobs: 87,
    completedJobs: 542,
    disputedJobs: 3,
    revenue: 45000,
    platformFees: 5400,
    commissionRate: 12,
  };



  const jobs = [
    {
      id: "1",
      customer: "Ali Mohammed",
      tasker: "Ahmed Plumbing",
      service: "Pipe Repair",
      amount: 150,
      date: "2024-12-24",
      status: "completed",
      disputed: false,
    },
    {
      id: "2",
      customer: "Sara Ahmed",
      tasker: "FastFix Services",
      service: "Drain Cleaning",
      amount: 180,
      date: "2024-12-24",
      status: "in-progress",
      disputed: false,
    },
    {
      id: "3",
      customer: "Khalid Hassan",
      tasker: "Emergency Plumbing",
      service: "Emergency Repair",
      amount: 250,
      date: "2024-12-23",
      status: "disputed",
      disputed: true,
    },
    {
      id: "4",
      customer: "Fatima Ali",
      tasker: "Mohammed Electrical",
      service: "Wiring",
      amount: 320,
      date: "2024-12-23",
      status: "completed",
      disputed: false,
    },
  ];

  const reviews = [
    {
      id: "1",
      customer: "Mohammed Ali",
      tasker: "Ahmed Plumbing",
      rating: 5,
      comment: "Excellent service!",
      date: "2024-12-24",
      flagged: false,
    },
    {
      id: "2",
      customer: "Sara Ahmed",
      tasker: "FastFix",
      rating: 4,
      comment: "Good work",
      date: "2024-12-23",
      flagged: false,
    },
    {
      id: "3",
      customer: "Ali Hassan",
      tasker: "Quick Services",
      rating: 1,
      comment: "Very unprofessional and rude",
      date: "2024-12-22",
      flagged: true,
    },
  ];

  const handleResolveDispute = (id, resolution) => {
    console.log("Resolving dispute:", id, "in favor of:", resolution);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#006C35] rounded-lg flex items-center justify-center">
                <span className="text-white">🔧</span>
              </div>
              <div>
                <span className="text-[#006C35]">TaskerConnect</span>
                <span className="text-sm text-gray-600 ml-2">Admin Panel</span>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-18 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-6 overflow-x-auto">
            {[
              { id: "dashboard", label: "Dashboard", icon: BarChart3 },
              { id: "users", label: "Users", icon: Users },
              { id: "jobs", label: "Jobs", icon: Briefcase },
              { id: "payments", label: "Payments", icon: DollarSign },
              { id: "reviews", label: "Reviews", icon: Flag },
              { id: "reports", label: "Reports", icon: FileText },
              { id: "messages", label: "Messages", icon: Mail },
              { id: "export", label: "Export", icon: Search }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-[#006C35] text-[#006C35]"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Icon size={20} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <>
            {/* Stats Grid */}
            <StatsCards />

            {/* Disputed Jobs */}
            {stats.disputedJobs > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="text-red-600" size={24} />
                  <h3 className="text-[#1E3A8A]">
                    Disputed Jobs Requiring Resolution ({stats.disputedJobs})
                  </h3>
                </div>
                <div className="space-y-3">
                  {jobs
                    .filter((j) => j.disputed)
                    .map((job) => (
                      <div key={job.id} className="bg-white p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-[#1E3A8A] mb-1">{job.service}</div>
                            <div className="text-sm text-gray-600">
                              Customer: {job.customer} • Tasker: {job.tasker}
                            </div>
                            <div className="text-sm text-gray-600">
                              Amount: {job.amount} SAR • Date: {job.date}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => handleResolveDispute(job.id, "customer")}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            Refund Customer
                          </button>
                          <button
                            onClick={() => handleResolveDispute(job.id, "tasker")}
                            className="px-4 py-2 bg-[#006C35] text-white rounded-lg hover:bg-[#005428] transition-colors text-sm"
                          >
                            Pay Tasker
                          </button>
                          <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm">
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Recent Activity Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Jobs */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Recent Jobs</h3>
                <div className="space-y-3">
                  {jobs.slice(0, 4).map((job) => (
                    <div key={job.id} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[#1E3A8A]">{job.service}</div>
                        <div className="text-[#006C35]">{job.amount} SAR</div>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        {job.customer} → {job.tasker}
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          job.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : job.status === "in-progress"
                            ? "bg-blue-100 text-blue-800"
                            : job.status === "disputed"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {job.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flagged Reviews */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Flagged Reviews</h3>
                <div className="space-y-3">
                  {reviews
                    .filter((r) => r.flagged)
                    .map((review) => (
                      <div
                        key={review.id}
                        className="p-3 border border-red-200 bg-red-50 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[#1E3A8A]">{review.customer}</div>
                          <Flag className="text-red-600" size={16} />
                        </div>
                        <div className="text-sm text-gray-600 mb-1">
                          Tasker: {review.tasker} • Rating: {review.rating}⭐
                        </div>
                        <div className="text-sm text-gray-700 mb-2">"{review.comment}"</div>
                        <div className="flex gap-2">
                          <button className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">
                            Remove
                          </button>
                          <button className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700">
                            Dismiss Flag
                          </button>
                        </div>
                      </div>
                    ))}
                  {reviews.filter((r) => r.flagged).length === 0 && (
                    <div className="text-gray-500 text-center py-4">No flagged reviews</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Users Management Tab */}
        {activeTab === "users" && (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <UsersTab />
          </div>
        )}

        {/* Jobs Management Tab */}
        {activeTab === "jobs" && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <JobsTab />
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <PaymentsTab />
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div className="max-w-7xl mx-auto px-4 py-8">
            <ReviewsTab />
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Generate Reports</h3>
                <div className="space-y-3">
                  {[
                    "User Growth Report",
                    "Revenue & Financial Report",
                    "Job Completion Statistics",
                    "Tasker Performance Report",
                    "Customer Satisfaction Report",
                    "Platform Health Report",
                  ].map((report, idx) => (
                    <button
                      key={idx}
                      className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">{report}</span>
                        <FileText className="text-gray-400" size={20} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-[#1E3A8A] mb-4">Compliance & Legal</h3>
                <div className="space-y-3">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700">KYC Compliance</span>
                      <CheckCircle2 className="text-green-600" size={20} />
                    </div>
                    <p className="text-sm text-gray-600">98% of taskers verified</p>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700">Tax Reporting</span>
                      <CheckCircle2 className="text-green-600" size={20} />
                    </div>
                    <p className="text-sm text-gray-600">Up to date for Q4 2024</p>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700">Data Protection</span>
                      <CheckCircle2 className="text-green-600" size={20} />
                    </div>
                    <p className="text-sm text-gray-600">GDPR compliant</p>
                  </div>

                  <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700">Insurance Coverage</span>
                      <Clock className="text-orange-600" size={20} />
                    </div>
                    <p className="text-sm text-gray-600">Renewal due in 30 days</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-[#1E3A8A] mb-4">Analytics Dashboard</h3>
              <p className="text-gray-600">Detailed analytics and insights coming soon...</p>
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <ContactMessages />
          </div>
        )}

        {/* Search Tab */}
        {activeTab === "export" && <ExportTab />}
      </div>
    </div>
  );
}
